/**
 * WsaWizard - Touch-optimierte WSA-Erfassung
 *
 * 4-Schritt Wizard:
 * 1. Maschine wählen (Button-Grid)
 * 2. Kunde wählen (Button-Grid)
 * 3. Programm wählen (Button-Grid)
 * 4. Gewicht + Bestätigung (Numpad)
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowLeft,
  Check,
  Scale,
  AlertTriangle,
  Loader2,
  WashingMachine,
  Building2,
  ListChecks,
  Search,
  Clock,
  Info,
  Link as LinkIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Stepper } from './Stepper';
import { GewichtRueckfrage, RUECKFRAGE_AB_KG } from './GewichtRueckfrage';

interface Machine {
  id: string;
  name: string;
  capacityKg: number;
  minLoadKg?: number;
  maxLoadKg?: number;
}

interface Customer {
  id: string;
  name: string;
  shortName: string | null;
  itemType?: 'customer' | 'group';
}

interface CustomerGroup {
  id: string;
  name: string;
  type: string;
  code?: string; // fester Gruppen-Code, z. B. BEWOHNERWAESCHE — kommt von der API mit
  systemCode?: string | null;
}

interface Program {
  id: string;
  number: string;
  name: string;
}

interface WsaWizardProps {
  stationId: string | null;
  isAdminMode?: boolean;
  onSuccess: () => void;
  onExtendUnlock: () => void;
  /** Karenz 18.08.: Nachzügler-Wahl der Station — Posten gehört noch zur eben beendeten Schicht. */
  vorherigeSchicht?: boolean;
}

type Step =
  'machine' | 'customer' | 'waescheart' | 'participants' | 'program' | 'input' | 'confirm';

// Flow Einzelkunde: Maschine -> Kunde -> Wäscheart -> Programm -> Gewicht
// Flow Sammelposten: Maschine -> Auswahl -> Teilnehmer -> Programm -> Gewicht
// (Beim Sammelposten sagt der Posten die Wäscheart selbst — keine Frage nötig.)
const STEPS = ['Maschine', 'Auswahl', 'Wäscheart', 'Programm', 'Gewicht'];
const STEPS_WITH_PARTICIPANTS = ['Maschine', 'Auswahl', 'Kunden', 'Programm', 'Gewicht'];
const STEP_INDEX: Record<Step, number> = {
  machine: 0,
  customer: 1,
  waescheart: 2, // nur bei normalem Kunden
  participants: 2, // (kommt in diesem Flow nicht vor)
  program: 3,
  input: 4,
  confirm: 4,
};
const STEP_INDEX_WITH_PARTICIPANTS: Record<Step, number> = {
  machine: 0,
  customer: 1,
  waescheart: 2, // (kommt in diesem Flow nicht vor)
  participants: 2,
  program: 3,
  input: 4,
  confirm: 4,
};

// Die Dreier-Frage beim Einzelkunden (Beschluss Richard, 14.08.2026):
// bewusst genau drei Antworten — "der Rest ist mir egal".
type WaescheartWert = 'BERUFSBEKLEIDUNG' | 'BEWOHNERWAESCHE' | 'SONSTIGES';
const WAESCHEART_OPTIONEN: Array<{ wert: WaescheartWert; label: string; hinweis: string }> = [
  { wert: 'BERUFSBEKLEIDUNG', label: 'Berufsbekleidung', hinweis: 'Kittel, Kasacks, Hosen …' },
  { wert: 'BEWOHNERWAESCHE', label: 'Bewohnerwäsche', hinweis: 'private Wäsche der Bewohner' },
  { wert: 'SONSTIGES', label: 'Alles andere', hinweis: 'Gästewäsche, Gardinen, Sonstiges' },
];
const WAESCHEART_LABELS: Record<WaescheartWert, string> = {
  BERUFSBEKLEIDUNG: 'Berufsbekleidung',
  BEWOHNERWAESCHE: 'Bewohnerwäsche',
  SONSTIGES: 'Alles andere',
};

// Gesammelte Kunden+Gewichte im Waschgang
interface WashRunEntry {
  customer: Customer;
  weightKg: number;
}

export function WsaWizard({
  stationId,
  isAdminMode,
  onSuccess,
  onExtendUnlock,
  vorherigeSchicht,
}: WsaWizardProps) {
  // Stammdaten
  const [machines, setMachines] = useState<Machine[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [topUsage, setTopUsage] = useState<
    Array<{ id: string; displayName: string; kind: 'customer' | 'group' }>
  >([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);

  // Kundensuche
  const [customerSearch, setCustomerSearch] = useState('');

  // Gefilterte Kunden + Gruppen (case-insensitive Suche)
  const filteredResults = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return [];

    // Gruppen durchsuchen
    const matchingGroups = customerGroups
      .filter((g) => g.name.toLowerCase().includes(q))
      .map((g) => ({
        id: `group:${g.id}`,
        name: g.name,
        shortName: null as string | null,
        itemType: 'group' as const,
      }));

    // Kunden durchsuchen
    const matchingCustomers = customers
      .filter((c) => c.name.toLowerCase().includes(q) || c.shortName?.toLowerCase().includes(q))
      .map((c) => ({
        ...c,
        itemType: (c.itemType || 'customer') as 'customer' | 'group',
      }));

    // Kombiniert, sortiert, limitiert
    return [...matchingGroups, ...matchingCustomers]
      .sort((a, b) => a.name.localeCompare(b.name, 'de'))
      .slice(0, 50);
  }, [customers, customerGroups, customerSearch]);

  // Wizard State
  const [step, setStep] = useState<Step>('machine');
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [waescheart, setWaescheart] = useState<WaescheartWert | null>(null);
  const [weight, setWeight] = useState('');

  // Waschgang-Gruppierung
  const [currentWashRunId, setCurrentWashRunId] = useState<string | null>(null);
  const [washRunEntries, setWashRunEntries] = useState<WashRunEntry[]>([]); // Gesammelte Kunden+Gewichte

  // SYSTEM_BUCKET: Teilnehmer-Kunden (ohne Gewicht)
  const [participants, setParticipants] = useState<Customer[]>([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantsExpanded, setParticipantsExpanded] = useState(false);
  const participantSearchRef = useRef<HTMLInputElement>(null);

  // Soft-Block Modal: Warnung wenn SYSTEM_BUCKET ohne Teilnehmer
  const [showNoParticipantsModal, setShowNoParticipantsModal] = useState(false);
  const [noParticipantsModalSource, setNoParticipantsModalSource] = useState<
    'participants' | 'confirm'
  >('participants');
  const [selectedOmitReason, setSelectedOmitReason] = useState<string>('');
  const [participantsSkipAcknowledged, setParticipantsSkipAcknowledged] = useState(false);

  // Prüfen ob aktuell ein Sammelposten (SYSTEM_BUCKET) gewählt ist
  const isSystemBucket =
    selectedCustomer?.id.startsWith('group:') &&
    customerGroups.find((g) => `group:${g.id}` === selectedCustomer?.id)?.type === 'SYSTEM_BUCKET';

  // Beim Bewohnerwäsche-Posten sind die Kunden Pflicht (Beschluss 27.03.2026,
  // umgesetzt 15.08.2026): Ohne gewählten Kunden gibt es keinen Weiter-Knopf —
  // auch keinen ausgegrauten. Weiß/Farbe/Sonder behalten das Überspringen.
  const istBewohnerwaesche =
    selectedCustomer?.id.startsWith('group:') &&
    customerGroups.find((g) => `group:${g.id}` === selectedCustomer?.id)?.code ===
      'BEWOHNERWAESCHE';

  // Auto-Fokus auf Teilnehmer-Suchfeld bei SYSTEM_BUCKET im participants-Step
  useEffect(() => {
    if (step === 'participants' && isSystemBucket) {
      // Kurze Verzögerung damit das Input gerendert ist
      setTimeout(() => participantSearchRef.current?.focus(), 100);
    }
  }, [step, isSystemBucket]);

  // Submit State
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState('');

  // Stammdaten laden
  const loadMasterData = useCallback(async () => {
    setLoading(true);
    try {
      // Top Usage (Quick-Buttons) - Auth ueber STATION-Cookie, Admin via stationId Query
      const topUrl =
        isAdminMode && stationId
          ? `/api/data/usage/top?stationId=${stationId}&limit=12`
          : '/api/data/usage/top?limit=12';

      const [machinesRes, customersRes, groupsRes, programsRes, topRes] = await Promise.all([
        fetch('/api/data/machines', { credentials: 'include' }),
        fetch('/api/data/customers', { credentials: 'include' }),
        fetch('/api/data/customer-groups', { credentials: 'include' }),
        fetch('/api/data/programs', { credentials: 'include' }),
        fetch(topUrl, { credentials: 'include' }),
      ]);

      if (machinesRes.ok) {
        const data = await machinesRes.json();
        setMachines(data.filter((m: Machine & { type?: string }) => m.type === 'WSA' || !m.type));
      }
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (groupsRes.ok) setCustomerGroups(await groupsRes.json());
      if (programsRes.ok) setPrograms(await programsRes.json());
      if (topRes?.ok) {
        setTopUsage(await topRes.json());
      }
    } catch (err) {
      console.error('Fehler beim Laden der Stammdaten:', err);
    } finally {
      setLoading(false);
    }
  }, [stationId, isAdminMode]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Navigation - Zurueck setzt abhaengige Schritte zurueck
  // Flow: machine -> customer -> [participants bei SYSTEM_BUCKET] -> program -> input -> confirm
  const goBack = () => {
    if (step === 'customer') {
      setStep('machine');
      setSelectedMachine(null);
      setSelectedCustomer(null);
      setWeight('');
      setWashRunEntries([]);
      setParticipants([]);
    } else if (step === 'participants') {
      setStep('customer');
      setSelectedCustomer(null);
      setParticipants([]);
      setParticipantSearch('');
    } else if (step === 'waescheart') {
      setStep('customer');
      setSelectedCustomer(null);
      setWaescheart(null);
    } else if (step === 'program') {
      // Bei SYSTEM_BUCKET zurück zu participants, sonst zur Wäscheart-Frage
      if (isSystemBucket) {
        setStep('participants');
      } else {
        setStep('waescheart');
        setWaescheart(null);
      }
      setSelectedProgram(null);
    } else if (step === 'input') {
      setStep('program');
      setSelectedProgram(null);
      setWeight('');
    } else if (step === 'confirm') {
      setStep('input');
    }
    setError('');
  };

  const resetForm = () => {
    setStep('machine');
    setSelectedMachine(null);
    setSelectedCustomer(null);
    setSelectedProgram(null);
    setWaescheart(null);
    setWeight('');
    setError('');
    setSuccess(false);
    // Neuer Waschgang
    setCurrentWashRunId(null);
    setWashRunEntries([]);
    // Teilnehmer zurücksetzen
    setParticipants([]);
    setParticipantSearch('');
    setParticipantsSkipAcknowledged(false);
  };

  // Generiert neue washRunId falls noch keine existiert
  const getOrCreateWashRunId = () => {
    if (currentWashRunId) return currentWashRunId;
    const newId = crypto.randomUUID();
    setCurrentWashRunId(newId);
    return newId;
  };

  // Gewichts-Validierung
  const weightNum = parseFloat(weight) || 0;
  const isOverloaded = selectedMachine?.maxLoadKg && weightNum > selectedMachine.maxLoadKg * 1.15;
  const isUnderloaded = selectedMachine?.minLoadKg && weightNum < selectedMachine.minLoadKg;

  // Aktuellen Kunden+Gewicht zur Liste hinzufügen und weiteren Kunden erfassen
  const handleAddCustomerAndContinue = () => {
    if (!selectedCustomer || !weight || parseFloat(weight) <= 0) return;

    // Kunden+Gewicht zur Liste hinzufügen
    setWashRunEntries((prev) => [
      ...prev,
      { customer: selectedCustomer, weightKg: parseFloat(weight) },
    ]);
    setToast(`${weight} kg hinzugefügt`);
    setTimeout(() => setToast(''), 2000);

    // Reset nur Kunde + Gewicht, Maschine bleibt
    setSelectedCustomer(null);
    setWeight('');
    setStep('customer');
  };

  // Speichern (neuer Flow: ein Kunde/Sammelposten pro Waschgang)
  const [zeigeGewichtRueckfrage, setZeigeGewichtRueckfrage] = useState(false);

  const handleSaveClick = () => {
    if (!selectedMachine || !selectedCustomer || !selectedProgram || !weight) return;

    // Weiche Rueckfrage bei ungewoehnlich hohem Gewicht — bestaetigen, nicht
    // sperren (die harte Grenze sitzt im Backend, gewicht-grenze.ts)
    if (parseFloat(weight) >= RUECKFRAGE_AB_KG) {
      setZeigeGewichtRueckfrage(true);
      return;
    }

    handleSaveWeiter();
  };

  // Die Pruefungen nach der Gewichts-Rueckfrage — auch deren Bestaetigung
  // muss durch Teilnehmerpflicht und Sammelposten-Softblock laufen.
  const handleSaveWeiter = () => {
    if (!selectedMachine || !selectedCustomer || !selectedProgram || !weight) return;

    // Sicherheitsnetz: Bewohnerwäsche ohne Kunden wird nie gespeichert —
    // der Teilnehmer-Schritt lässt das zwar nicht mehr zu, aber falls doch
    // ein Weg daran vorbeiführt, bricht das Speichern hier ab.
    if (istBewohnerwaesche && participants.length === 0) {
      setError('Bei Bewohnerwäsche müssen die Kunden angegeben werden.');
      setStep('participants');
      return;
    }

    // Soft-Block: Bei SYSTEM_BUCKET ohne Teilnehmer -> Modal anzeigen
    // Aber nicht nochmal wenn User schon auf Participants-Step bestaetigt hat
    if (isSystemBucket && participants.length === 0 && !participantsSkipAcknowledged) {
      setNoParticipantsModalSource('confirm');
      setShowNoParticipantsModal(true);
      return;
    }

    // Wurde das Überspringen schon im Teilnehmer-Schritt bestätigt, den dort
    // gewählten Grund mitgeben — er ging bisher auf diesem Pfad verloren.
    if (isSystemBucket && participants.length === 0 && participantsSkipAcknowledged) {
      doSave(selectedOmitReason || 'UNSPECIFIED');
      return;
    }

    // Sonst direkt speichern
    doSave();
  };

  // Tatsaechliches Speichern (mit optionalem Grund)
  const doSave = async (omitReason?: string) => {
    // Doppel-Submit-Sperre (analog FlWizard): schützt auch Pfade, die nicht
    // über disabled-Buttons laufen
    if (submitting) return;
    console.log('[doSave] called with:', {
      omitReason,
      machine: !!selectedMachine,
      customer: !!selectedCustomer,
      program: !!selectedProgram,
      weight,
    });
    if (!selectedMachine || !selectedCustomer || !selectedProgram || !weight) {
      console.warn('[doSave] EARLY RETURN - missing:', {
        machine: !selectedMachine,
        customer: !selectedCustomer,
        program: !selectedProgram,
        weight: !weight,
      });
      return;
    }

    setSubmitting(true);
    setError('');
    setShowNoParticipantsModal(false);

    try {
      const url = isAdminMode ? '/api/wsa-entries' : `/api/stations/${stationId}/wsa-entries`;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          machineId: selectedMachine.id,
          customerId: selectedCustomer.id,
          programId: selectedProgram.id,
          weightKg: parseFloat(weight),
          // Teilnehmer-IDs bei SYSTEM_BUCKET
          participantIds: isSystemBucket ? participants.map((p) => p.id) : undefined,
          // Grund wenn ohne Teilnehmer gespeichert
          participantsOmittedReason: omitReason || undefined,
          // Dreier-Frage beim Einzelkunden; Sammelposten bestimmen die Wäscheart selbst
          waescheart: !isSystemBucket && waescheart ? waescheart : undefined,
          ...(vorherigeSchicht ? { vorherigeSchicht: true } : {}),
          ...(isAdminMode && stationId ? { stationId } : {}),
        }),
      });

      if (res.ok) {
        onExtendUnlock();
        onSuccess();
        resetForm();
        setSelectedOmitReason('');
      } else {
        const data = await res.json();
        setError(data.error || 'Fehler beim Speichern');
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setSubmitting(false);
    }
  };

  // Numpad Handler
  const handleNumpad = (key: string) => {
    if (key === '←') {
      setWeight((prev) => prev.slice(0, -1));
    } else if (key === '.') {
      if (!weight.includes('.')) setWeight((prev) => prev + '.');
    } else if (key === 'C') {
      setWeight('');
    } else {
      setWeight((prev) => prev + key);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-akzent" />
      </div>
    );
  }

  // Erfolgs-Anzeige
  if (success) {
    return (
      <div className="text-center py-8">
        <div className="w-20 h-20 bg-flaeche-2 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-10 h-10 text-lauf" />
        </div>
        <h3 className="text-xl font-bold text-tinte">Erfassung gespeichert!</h3>
        <p className="text-tinte-3 mt-2">{weight} kg erfasst</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stepper - erweitert bei SYSTEM_BUCKET */}
      <Stepper
        steps={isSystemBucket ? STEPS_WITH_PARTICIPANTS : STEPS}
        currentStep={isSystemBucket ? STEP_INDEX_WITH_PARTICIPANTS[step] : STEP_INDEX[step]}
      />

      {/* Fehler */}
      {error && (
        <div className="p-3 bg-flaeche-2 border border-linie rounded-lg text-stoer text-sm">
          {error}
        </div>
      )}

      {/* Zurück-Button - Legacy: groß, volle Breite auf Mobile */}
      {step !== 'machine' && (
        <button
          onClick={goBack}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 text-lg border border-linie-2 rounded-lg text-tinte-2 hover:bg-flaeche-2 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>
      )}

      {/* Aktuelle Auswahl - Legacy Badge Style */}
      {(selectedMachine || selectedCustomer || selectedProgram || weight) && (
        <div className="bg-flaeche rounded-lg border p-3">
          <div className="flex flex-wrap gap-2">
            {selectedMachine && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <WashingMachine className="w-3 h-3" />
                {selectedMachine.name}
              </span>
            )}
            {selectedCustomer && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Building2 className="w-3 h-3" />
                {selectedCustomer.shortName || selectedCustomer.name}
              </span>
            )}
            {selectedProgram && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <ListChecks className="w-3 h-3" />
                {selectedProgram.number}
              </span>
            )}
            {weight && (
              <span className="inline-flex items-center gap-1 px-3 py-1 border rounded-full text-sm">
                <Scale className="w-3 h-3" />
                {weight} kg
              </span>
            )}
          </div>
        </div>
      )}

      {/* Schritt 1: Maschine - Legacy Card Style */}
      {step === 'machine' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <WashingMachine className="w-5 h-5" />
              Maschine wählen
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {machines.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    setSelectedMachine(m);
                    setStep('customer');
                  }}
                  className="min-h-[72px] text-lg border border-linie rounded-lg hover:bg-flaeche-2 transition-colors flex flex-col items-center justify-center gap-1"
                >
                  <span>{m.name}</span>
                  <span className="text-xs text-tinte-3">{m.capacityKg} kg</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schritt 2: Kunde/Sammelposten wählen */}
      {step === 'customer' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Kunde wählen
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {/* Sammelposten (SYSTEM_BUCKET) - prominent oben */}
            {customerGroups.filter((g) => g.type === 'SYSTEM_BUCKET').length > 0 && (
              <div>
                <div className="text-sm text-tinte-2 font-medium mb-2">Sammelposten</div>
                <div className="grid grid-cols-3 gap-2">
                  {customerGroups
                    .filter((g) => g.type === 'SYSTEM_BUCKET')
                    .sort((a, b) => a.name.localeCompare(b.name, 'de'))
                    .map((g) => (
                      <button
                        key={g.id}
                        onClick={() => {
                          setSelectedCustomer({
                            id: `group:${g.id}`,
                            name: g.name,
                            shortName: null,
                            itemType: 'group',
                          });
                          setCustomerSearch('');
                          setStep('participants'); // Bei SYSTEM_BUCKET zuerst Teilnehmer
                        }}
                        className="min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 bg-flaeche-2 border border-linie hover:bg-flaeche-2"
                      >
                        {g.name}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Trennlinie */}
            {customerGroups.filter((g) => g.type === 'SYSTEM_BUCKET').length > 0 && (
              <div className="border-t border-linie pt-4">
                <div className="text-sm text-tinte-3 font-medium mb-2">Oder Kunde wählen</div>
              </div>
            )}

            {/* Suchfeld */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tinte-3" />
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Kunde suchen..."
                className="w-full pl-10 pr-4 py-3 text-lg border border-linie-2 rounded-lg focus:ring-2 focus:ring-akzent focus:border-akzent"
                autoFocus
              />
            </div>

            {/* Keine Suche: Quick-Buttons (alphabetisch sortiert) anzeigen */}
            {!customerSearch.trim() && topUsage.length > 0 && (
              <div>
                <div className="flex items-center gap-2 text-sm text-tinte-3 mb-2">
                  <Clock className="w-4 h-4" />
                  Haeufig verwendet
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {topUsage.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        // id kann "group:xxx" oder normale customerId sein
                        setSelectedCustomer({
                          id: item.id,
                          name: item.displayName,
                          shortName: null,
                        });
                        setCustomerSearch('');
                        // Einzelkunde → Dreier-Frage. Sammelposten → Teilnehmer-Schritt,
                        // wie beim Kachel-Weg — bisher entfiel er hier (Bug, 15.08. behoben).
                        const bucket =
                          customerGroups.find((g) => `group:${g.id}` === item.id)?.type ===
                          'SYSTEM_BUCKET';
                        setStep(
                          item.id.startsWith('group:')
                            ? bucket
                              ? 'participants'
                              : 'program'
                            : 'waescheart',
                        );
                      }}
                      className={`min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 ${
                        item.kind === 'group'
                          ? 'bg-flaeche-2 border border-linie hover:bg-flaeche-2'
                          : 'bg-akzent-tief border border-akzent-tief hover:bg-akzent-tief'
                      }`}
                    >
                      {item.displayName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Keine Suche + keine Quick-Buttons: Hinweis */}
            {!customerSearch.trim() &&
              topUsage.length === 0 &&
              (isAdminMode && !stationId ? (
                <div className="bg-akzent-tief border border-akzent-tief rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-akzent mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-akzent">
                        "Zuletzt verwendet" ist im Admin-Testmodus ohne gekoppelte Station
                        deaktiviert.
                      </p>
                      <a
                        href={`/pair?returnTo=${encodeURIComponent(window.location.pathname)}`}
                        className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium text-akzent hover:text-akzent"
                      >
                        <LinkIcon className="w-4 h-4" />
                        Dieses Gerät koppeln
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-tinte-3 py-4">
                  Nutzen Sie die Suche, um Kunden zu finden
                </p>
              ))}

            {/* Mit Suche: gefilterte Liste (Kunden + Gruppen) */}
            {customerSearch.trim() && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                {filteredResults.length === 0 ? (
                  <p className="col-span-full text-center text-tinte-3 py-4">
                    Keine Treffer für "{customerSearch}"
                  </p>
                ) : (
                  filteredResults.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setSelectedCustomer(c);
                        setCustomerSearch('');
                        // Einzelkunde → Dreier-Frage. Sammelposten → Teilnehmer-Schritt,
                        // wie beim Kachel-Weg — bisher entfiel er hier (Bug, 15.08. behoben).
                        const bucket =
                          customerGroups.find((g) => `group:${g.id}` === c.id)?.type ===
                          'SYSTEM_BUCKET';
                        setStep(
                          c.id.startsWith('group:')
                            ? bucket
                              ? 'participants'
                              : 'program'
                            : 'waescheart',
                        );
                      }}
                      className={`min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 ${
                        c.itemType === 'group'
                          ? 'bg-flaeche-2 border border-linie hover:bg-flaeche-2'
                          : 'bg-akzent-tief border border-akzent-tief hover:bg-akzent-tief'
                      }`}
                    >
                      {c.shortName || c.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schritt 3: Teilnehmer (nur bei SYSTEM_BUCKET) */}
      {step === 'participants' && isSystemBucket && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Kunden im Sammelposten
            </h3>
            <p className="text-sm text-tinte-3 mt-1">
              Welche Kunden sind in diesem Sammelposten enthalten?
            </p>
          </div>
          <div className="p-4 space-y-4">
            {/* Info */}
            <div className="p-3 bg-flaeche-2 border border-linie rounded-lg">
              <div className="text-sm text-stoer">
                <strong>{selectedCustomer?.name}</strong> - Kunden hinzufügen
              </div>
            </div>

            {/* Bereits hinzugefügte Teilnehmer */}
            {participants.length > 0 && (
              <div className="space-y-1">
                <div className="text-sm text-tinte-2 font-medium">
                  Hinzugefügt ({participants.length}):
                </div>
                {participants.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-flaeche rounded-lg px-3 py-2 text-sm border border-linie"
                  >
                    <span className="font-medium">{p.shortName || p.name}</span>
                    <button
                      onClick={() => setParticipants((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-stoer hover:text-stoer text-sm font-medium"
                    >
                      Entfernen
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Suchfeld */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-tinte-3" />
              <input
                ref={participantSearchRef}
                type="text"
                value={participantSearch}
                onChange={(e) => setParticipantSearch(e.target.value)}
                placeholder="Kunde suchen..."
                className="w-full pl-10 pr-4 py-3 text-lg border border-linie-2 rounded-lg focus:ring-2 focus:ring-akzent focus:border-akzent"
              />
            </div>

            {/* Keine Suche: Schnellwahl */}
            {!participantSearch.trim() &&
              topUsage.filter(
                (t) => t.kind === 'customer' && !participants.find((p) => p.id === t.id),
              ).length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-sm text-tinte-3 mb-2">
                    <Clock className="w-4 h-4" />
                    Haeufig verwendet
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {topUsage
                      .filter(
                        (t) => t.kind === 'customer' && !participants.find((p) => p.id === t.id),
                      )
                      .map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            const customer = customers.find((c) => c.id === item.id);
                            if (customer) {
                              setParticipants((prev) => [...prev, customer]);
                              participantSearchRef.current?.focus();
                            }
                          }}
                          className="min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 bg-akzent-tief border border-akzent-tief hover:bg-akzent-tief"
                        >
                          {item.displayName}
                        </button>
                      ))}
                  </div>
                </div>
              )}

            {/* Mit Suche: gefilterte Liste */}
            {participantSearch.trim() && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                {customers
                  .filter(
                    (c) =>
                      (c.name.toLowerCase().includes(participantSearch.toLowerCase()) ||
                        c.shortName?.toLowerCase().includes(participantSearch.toLowerCase())) &&
                      !participants.find((p) => p.id === c.id),
                  )
                  .slice(0, 12)
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setParticipants((prev) => [...prev, c]);
                        setParticipantSearch('');
                        participantSearchRef.current?.focus();
                      }}
                      className="min-h-[56px] text-sm font-medium rounded-lg transition-colors px-2 bg-flaeche border border-linie hover:bg-akzent-tief hover:border-akzent-tief"
                    >
                      {c.shortName || c.name}
                    </button>
                  ))}
                {customers.filter(
                  (c) =>
                    (c.name.toLowerCase().includes(participantSearch.toLowerCase()) ||
                      c.shortName?.toLowerCase().includes(participantSearch.toLowerCase())) &&
                    !participants.find((p) => p.id === c.id),
                ).length === 0 && (
                  <p className="col-span-full text-center text-tinte-3 py-4">Keine Treffer</p>
                )}
              </div>
            )}

            {/* Weiter-Button. Bei Bewohnerwäsche erscheint er erst, wenn
                mindestens ein Kunde gewählt ist — ohne Kunden geht es dort
                nicht weiter, und ein ausgegrauter Knopf soll auch nicht stehen. */}
            {(participants.length > 0 || !istBewohnerwaesche) && (
              <button
                onClick={() => {
                  if (participants.length === 0) {
                    setNoParticipantsModalSource('participants');
                    setShowNoParticipantsModal(true);
                  } else {
                    setStep('program');
                  }
                }}
                className="w-full mt-4 min-h-[56px] text-base bg-akzent text-akzent-satz rounded-lg hover:bg-akzent transition-colors"
              >
                {participants.length === 0
                  ? 'Weiter ohne Kunden'
                  : `Weiter mit ${participants.length} Kunde${participants.length > 1 ? 'n' : ''}`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Schritt 4: Gewicht eingeben */}
      {step === 'input' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <Scale className="w-5 h-5" />
              Gewicht eingeben
            </h3>
          </div>
          <div className="p-4">
            {/* Auswahl-Info */}
            <div className="mb-4 p-3 bg-flaeche-2 border border-linie rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-tinte-3">Auswahl:</span>
                <span className="font-medium">{selectedCustomer?.name}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-tinte-3">Programm:</span>
                <span className="font-medium">
                  {selectedProgram?.number} - {selectedProgram?.name}
                </span>
              </div>
            </div>

            {/* Gewichtsanzeige */}
            <div className="text-center mb-6">
              <div
                className={`text-5xl font-bold ${
                  isOverloaded ? 'text-stoer' : isUnderloaded ? 'text-stand' : ''
                }`}
              >
                {weight || '0'}
                <span className="text-2xl ml-2 text-tinte-3">kg</span>
              </div>
              {selectedMachine && (selectedMachine.minLoadKg || selectedMachine.maxLoadKg) && (
                <div className="text-sm text-tinte-3 mt-2">
                  {selectedMachine.minLoadKg && `Min: ${selectedMachine.minLoadKg} kg`}
                  {selectedMachine.minLoadKg && selectedMachine.maxLoadKg && ' | '}
                  {selectedMachine.maxLoadKg && `Max: ${selectedMachine.maxLoadKg} kg`}
                </div>
              )}
              {isOverloaded && (
                <div className="text-stoer flex items-center justify-center gap-1 mt-2">
                  <AlertTriangle className="w-4 h-4" />
                  Überladen!
                </div>
              )}
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
              {['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0', '←'].map((key) => (
                <button
                  key={key}
                  onClick={() => handleNumpad(key)}
                  className="min-h-[56px] text-2xl font-bold bg-flaeche border border-linie rounded-lg hover:bg-flaeche-2 active:bg-flaeche-2 transition-colors"
                >
                  {key}
                </button>
              ))}
            </div>

            {/* Weiter-Button */}
            <button
              onClick={() => setStep('confirm')}
              disabled={!(parseFloat(weight) > 0)}
              className="w-full mt-4 min-h-[56px] text-base bg-akzent text-akzent-satz rounded-lg hover:bg-akzent disabled:bg-linie-2 disabled:cursor-not-allowed transition-colors"
            >
              Weiter
            </button>
          </div>
        </div>
      )}

      {/* Schritt: Wäscheart (nur Einzelkunde) — Beschluss Richard, 14.08.2026.
          Kachel antippen wählt und geht direkt weiter, wie bei Maschine/Kunde. */}
      {step === 'waescheart' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte">Was ist es?</h3>
            <p className="text-sm text-tinte-3 mt-0.5">
              {selectedCustomer?.shortName || selectedCustomer?.name}
            </p>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            {WAESCHEART_OPTIONEN.map((o) => (
              <button
                key={o.wert}
                onClick={() => {
                  setWaescheart(o.wert);
                  setStep('program');
                }}
                className="min-h-[88px] rounded-lg border border-linie hover:bg-akzent-tief hover:border-akzent-tief transition-colors flex flex-col items-center justify-center gap-1 px-3"
              >
                <span className="text-lg font-semibold text-tinte">{o.label}</span>
                <span className="text-xs text-tinte-3">{o.hinweis}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Schritt 3: Programm wählen */}
      {step === 'program' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <ListChecks className="w-5 h-5" />
              Programm wählen
            </h3>
          </div>
          <div className="p-4">
            {/* Auswahl-Info */}
            <div className="mb-4 p-3 bg-flaeche-2 border border-linie rounded-lg">
              <div className="text-sm text-tinte-2">
                <strong>{selectedCustomer?.name}</strong>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {programs.map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    setSelectedProgram(p);
                    setStep('input');
                  }}
                  className="min-h-[72px] text-lg border border-linie rounded-lg hover:bg-flaeche-2 transition-colors flex flex-col items-center justify-center gap-1"
                >
                  <span className="font-bold">{p.number}</span>
                  <span className="text-xs text-tinte-3">{p.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Schritt 5: Bestätigung - Übersicht + Speichern */}
      {step === 'confirm' && (
        <div className="bg-flaeche rounded-lg border shadow-sm">
          <div className="px-4 py-3 border-b">
            <h3 className="text-lg font-semibold text-tinte flex items-center gap-2">
              <Check className="w-5 h-5" />
              Waschgang speichern
            </h3>
          </div>
          <div className="p-4">
            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b">
                <span className="text-tinte-3">Maschine:</span>
                <span className="font-medium">{selectedMachine?.name}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-tinte-3">Auswahl:</span>
                <span className="font-medium">{selectedCustomer?.name}</span>
              </div>
              {!isSystemBucket && waescheart && (
                <div className="flex justify-between py-2 border-b">
                  <span className="text-tinte-3">Wäscheart:</span>
                  <span className="font-medium">{WAESCHEART_LABELS[waescheart]}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-tinte-3">Programm:</span>
                <span className="font-medium">
                  {selectedProgram?.number} - {selectedProgram?.name}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-tinte-3">Gewicht:</span>
                <span className="font-bold text-xl text-akzent">{weight} kg</span>
              </div>

              {/* Aufklappbare Teilnehmer-Liste */}
              {isSystemBucket && participants.length > 0 && (
                <div className="py-2 border-b">
                  <button
                    onClick={() => setParticipantsExpanded(!participantsExpanded)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="text-tinte-3">Kunden ({participants.length}):</span>
                    {participantsExpanded ? (
                      <ChevronDown className="w-4 h-4 text-tinte-3" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-tinte-3" />
                    )}
                  </button>
                  {participantsExpanded && (
                    <div className="mt-2 space-y-1 pl-2">
                      {participants.map((p, i) => (
                        <div key={i} className="text-sm text-tinte-2">
                          {p.shortName || p.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 bg-flaeche-2 border border-linie rounded-lg text-sm text-stoer">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={goBack}
                disabled={submitting}
                className="flex-1 min-h-[56px] text-base border border-linie-2 text-tinte-2 rounded-lg hover:bg-flaeche-2 disabled:bg-flaeche-2 transition-colors"
              >
                Zurück
              </button>
              <button
                onClick={handleSaveClick}
                disabled={submitting || !weight || parseFloat(weight) <= 0}
                className="flex-1 min-h-[56px] text-base bg-akzent text-akzent-satz rounded-lg hover:bg-akzent disabled:bg-linie-2 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-lauf text-akzent-satz rounded-lg shadow-lg font-medium">
          {toast}
        </div>
      )}

      {/* Soft-Block Modal: Warnung wenn SYSTEM_BUCKET ohne Teilnehmer */}
      {zeigeGewichtRueckfrage && (
        <GewichtRueckfrage
          kg={parseFloat(weight) || 0}
          onZurueck={() => setZeigeGewichtRueckfrage(false)}
          onBestaetigen={() => {
            setZeigeGewichtRueckfrage(false);
            handleSaveWeiter();
          }}
        />
      )}

      {showNoParticipantsModal &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-flaeche rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-flaeche-2 flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-stand" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-tinte">Keine Kunden erfasst</h3>
                    <p className="text-sm text-tinte-3">Sammelposten ohne Kunden-Zuordnung</p>
                  </div>
                </div>

                <p className="text-tinte-2 mb-4">
                  Sie sind dabei, einen Sammelposten ohne Kunden zu speichern. Fuer die
                  Nachverfolgung empfehlen wir, mindestens einen Kunden hinzuzufuegen.
                </p>

                {/* Grund-Auswahl */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-tinte-2 mb-2">
                    Grund (optional)
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'UNKNOWN', label: 'Kunden unbekannt' },
                      { value: 'TIME_PRESSURE', label: 'Zeitdruck' },
                      { value: 'OTHER', label: 'Sonstiges' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSelectedOmitReason(option.value)}
                        className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                          selectedOmitReason === option.value
                            ? 'border-stand bg-flaeche-2 text-stoer'
                            : 'border-linie hover:border-linie-2 text-tinte-2'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      setShowNoParticipantsModal(false);
                      setSelectedOmitReason('');
                    }}
                    className="w-full min-h-[56px] text-base bg-akzent text-akzent-satz rounded-lg hover:bg-akzent font-medium transition-colors"
                  >
                    Kunden hinzufuegen
                  </button>
                  <button
                    onClick={() => {
                      console.log(
                        '[NoParticipantsModal] Button clicked, source:',
                        noParticipantsModalSource,
                      );
                      if (noParticipantsModalSource === 'participants') {
                        // Vom Participants-Step: weiter zum Programm-Step
                        setShowNoParticipantsModal(false);
                        setParticipantsSkipAcknowledged(true);
                        setStep('program');
                      } else {
                        // Vom Confirm-Step: direkt speichern
                        doSave(selectedOmitReason || 'UNSPECIFIED');
                      }
                    }}
                    disabled={submitting}
                    className="w-full min-h-[48px] text-sm border border-stoer text-stoer rounded-lg hover:bg-flaeche-2 font-medium transition-colors disabled:opacity-50"
                  >
                    {submitting
                      ? 'Speichern...'
                      : noParticipantsModalSource === 'participants'
                        ? 'Ohne Kunden fortfahren'
                        : 'Ohne Kunden speichern'}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
